<template>
  <div class="study-record-page">
    <skill-header title="学习记录" @back="handleBack" />

    <main class="study-record-content">
      <nav
        v-if="stations.length"
        ref="stationTabs"
        class="station-tabs"
        aria-label="考站切换"
      >
        <button
          v-for="(station, index) in stations"
          :key="stationKey(station, index)"
          type="button"
          class="station-tab"
          :class="{ active: index === activeStationIndex }"
          @click="selectStation(index)"
        >
          {{ stationLabel(station, index) }}
        </button>
      </nav>

      <section v-if="loading" class="page-state">
        <span class="loading-spinner" />
        <p>加载中...</p>
      </section>
      <section v-else-if="errorText" class="page-state">
        <p>{{ errorText }}</p>
        <button type="button" class="retry-button" @click="loadPage">重新加载</button>
      </section>
      <section v-else-if="visibleRecords.length" class="record-list">
        <article
          v-for="(record, index) in visibleRecords"
          :key="recordKey(record, index)"
          class="record-card"
          :class="{ 'record-card--question': isQuestionRecord(record) }"
        >
          <h2>{{ recordTitle(record) }}</h2>
          <div class="record-divider" />
          <div class="record-copy">
            <template v-if="isQuestionRecord(record)">
              <p><span>刷题时长：</span>{{ formatDuration(record.duration) }}</p>
              <p><span>刷题数量：</span>{{ numberOrDash(record.questionCount) }}</p>
              <p><span>刷题时间：</span>{{ formatDate(record.studyTime || record.createTime) }}</p>
            </template>
            <template v-else>
              <p><span>训练时长：</span>{{ formatDuration(record.duration) }}</p>
              <p><span>训练时间：</span>{{ formatDate(record.studyTime || record.createTime) }}</p>
            </template>
          </div>
          <div
            class="record-gauge"
            :class="{ 'record-gauge--accuracy': isQuestionRecord(record) }"
          >
            <svg viewBox="0 0 60 60" aria-hidden="true">
              <circle class="record-gauge__track" cx="30" cy="30" r="28" pathLength="100" />
              <circle
                class="record-gauge__progress"
                cx="30"
                cy="30"
                r="28"
                pathLength="100"
                :stroke-dasharray="gaugeDasharray(record)"
              />
            </svg>
            <div class="record-gauge__inner">
              <strong>{{ gaugeValue(record) }}</strong>
              <small v-if="isQuestionRecord(record)">正确率</small>
            </div>
          </div>
        </article>
      </section>
      <section v-else class="page-state page-state--empty">
        <p>暂无学习记录</p>
      </section>
    </main>
  </div>
</template>

<script>
import { Toast } from 'vant'
import {
  getSkillCenterFullTree,
  getSkillCenterPracticeRecordList
} from '@/api/subject/index.js'
import { closeSkillCenterWeb } from '@/utils/skillCenter'
import { isAuthError, waitForAppToken } from '@/utils/appToken'
import SkillHeader from './SkillHeader.vue'
import {
  findSkillCenterCategory,
  nestedResourceTree,
  nodeOrganCategoryId,
  skillCenterTreeData,
  treeChildren
} from './shared'

export default {
  name: 'SkillCenterStudyRecords',
  components: { SkillHeader },
  data() {
    return {
      loading: true,
      errorText: '',
      stations: [],
      allRecords: [],
      activeStationIndex: 0,
      currentCategory: null,
      authExitTimer: null,
      authExpiredHandler: null
    }
  },
  computed: {
    majorType() {
      return this.$route.query.majorType || ''
    },
    categoryOrganId() {
      return this.$route.query.categoryOrganId || ''
    },
    organCategoryId() {
      return this.$route.query.organCategoryId || ''
    },
    selectedStation() {
      return this.stations[this.activeStationIndex] || null
    },
    visibleRecords() {
      if (!this.selectedStation || this.stations.length === 1) return this.allRecords
      return this.allRecords.filter((record) => this.recordBelongsToStation(record, this.selectedStation))
    }
  },
  async created() {
    if (String(this.$route.query.preview) === '1') {
      this.loadPreview()
      return
    }
    this.authExpiredHandler = () => this.handleAuthFailure()
    window.addEventListener('skillCenterAuthExpired', this.authExpiredHandler)
    try {
      await waitForAppToken()
      await this.loadPage()
    } catch (error) {
      this.handleAuthFailure()
    }
  },
  beforeDestroy() {
    if (this.authExpiredHandler) {
      window.removeEventListener('skillCenterAuthExpired', this.authExpiredHandler)
    }
    if (this.authExitTimer) clearTimeout(this.authExitTimer)
  },
  methods: {
    async loadPage() {
      this.loading = true
      this.errorText = ''
      const toast = Toast.loading({ message: '加载中', duration: 0, forbidClick: true })
      try {
        const treeResponse = await getSkillCenterFullTree()
        const tree = skillCenterTreeData(treeResponse)
        this.setStations(tree)
        const records = await this.loadRecords()
        this.allRecords = records
      } catch (error) {
        if (isAuthError(error)) {
          this.handleAuthFailure()
          return
        }
        this.errorText = '学习记录加载失败，请重试'
      } finally {
        toast.clear()
        this.loading = false
      }
    },
    async loadRecords() {
      const response = await getSkillCenterPracticeRecordList({
        pageNum: 1,
        pageSize: 1000,
        recordType: 1,
        platformTypes: '1,3',
        skillType: '',
        isExam: ''
      })
      return this.getRows(response).map((item) => ({
        ...item,
        sourceType: 'PRACTICE',
        courseName: item.courseName || item.name || item.skillName,
        duration: item.duration === undefined ? item.practiceTime : item.duration,
        studyTime: item.studyTime || item.createTime || item.practiceDate || item.startTime,
        score: item.score
      }))
    },
    setStations(tree) {
      const category = findSkillCenterCategory(tree, this.categoryOrganId, this.majorType)
      this.currentCategory = category
      this.stations = treeChildren(category).filter((item) => {
        return item && item.virtualNode === false && Number(item.storageLevel) === 2
      })
      if (!this.stations.length && category) {
        this.stations = treeChildren(category)
      }
      const selectedIndex = this.stations.findIndex((station) => {
        return nodeOrganCategoryId(station) === String(this.organCategoryId || '')
      })
      this.activeStationIndex = selectedIndex >= 0 ? selectedIndex : 0
    },
    selectStation(index) {
      if (index < 0 || index >= this.stations.length) return
      this.activeStationIndex = index
      this.$nextTick(() => this.moveStationTabs(index))
    },
    moveStationTabs(index) {
      const tabs = this.$refs.stationTabs
      if (!tabs || this.stations.length <= 3 || !tabs.children.length) return
      const tabWidth = tabs.children[0].getBoundingClientRect().width
      if (!tabWidth) return
      const firstVisibleIndex = Math.round(tabs.scrollLeft / tabWidth)
      const lastScrollableIndex = this.stations.length - 3
      const maxScrollLeft = tabs.scrollWidth - tabs.clientWidth
      const relativeIndex = index - firstVisibleIndex
      const moveCount = firstVisibleIndex >= lastScrollableIndex
        ? ({ 0: -2, 1: -1 }[relativeIndex] || 0)
        : ({ 1: 1, 2: 2 }[relativeIndex] || 0)
      const targetIndex = Math.max(
        0,
        Math.min(firstVisibleIndex + moveCount, lastScrollableIndex)
      )
      const targetLeft = Math.min(targetIndex * tabWidth, maxScrollLeft)
      if (Math.abs(targetLeft - tabs.scrollLeft) < 1) return
      if (typeof tabs.scrollTo === 'function') {
        tabs.scrollTo({ left: targetLeft, behavior: 'smooth' })
      } else {
        tabs.scrollLeft = targetLeft
      }
    },
    stationKey(station, index) {
      return nodeOrganCategoryId(station) || station.code || index
    },
    stationLabel(station, index) {
      const label = (station && (station.label || station.name || station.categoryName)) || ''
      return /^第.+考站$/.test(label) ? label.replace('考站', '站') : label || `第${index + 1}站`
    },
    recordKey(record, index) {
      return record && (record.recordKey || `${record.sourceType || 'record'}_${record.recordId || record.id}`) || `record_${index}`
    },
    recordTitle(record) {
      return record && (record.courseName || record.name || record.skillName) || '学习记录'
    },
    getRows(response) {
      if (Array.isArray(response)) return response
      const data = response && response.data !== undefined ? response.data : response
      if (Array.isArray(data)) return data
      if (data && Array.isArray(data.rows)) return data.rows
      if (data && Array.isArray(data.records)) return data.records
      return []
    },
    isQuestionRecord(record) {
      const sourceType = String(record && record.sourceType || '').toUpperCase()
      const mode = String(record && record.learningMode || '')
      return sourceType === 'STUDY' || sourceType === 'PAPER' || mode.includes('题库') || mode.includes('刷题')
    },
    stationTokens(station) {
      const tokens = []
      const visit = (node) => {
        if (!node) return
        const label = node.label || node.name || node.categoryName || node.title
        if (label && !/^第.+(站|考站)$/.test(label)) tokens.push(String(label).trim())
        treeChildren(node).forEach(visit)
        if (Array.isArray(node.skill && node.skill.tree)) node.skill.tree.forEach(visit)
        if (Array.isArray(node.question && node.question.tree)) node.question.tree.forEach(visit)
        if (Array.isArray(node.skills)) node.skills.forEach(visit)
      }
      visit(station)
      return Array.from(new Set(tokens.filter((token) => token.length > 1)))
    },
    recordBelongsToStation(record, station) {
      const title = this.recordTitle(record)
      const tokens = this.stationTokens(station)
      if (tokens.some((token) => title === token || title.includes(token) || token.includes(title))) return true
      const stationHasQuestions = nestedResourceTree(station, 'question').length > 0
      const stationHasSkills = nestedResourceTree(station, 'skill').length > 0
      const compatibleStationCount = this.stations.filter((item) => {
        const hasQuestions = nestedResourceTree(item, 'question').length > 0
        const hasSkills = nestedResourceTree(item, 'skill').length > 0
        return this.isQuestionRecord(record) ? hasQuestions : hasSkills
      }).length
      if (compatibleStationCount === 1 && stationHasQuestions && this.isQuestionRecord(record)) return true
      if (compatibleStationCount === 1 && stationHasSkills && !this.isQuestionRecord(record)) return true
      return false
    },
    numberOrDash(value) {
      return value === undefined || value === null || value === '' ? '--' : value
    },
    formatDate(value) {
      if (!value) return '--'
      const text = String(value)
      const match = text.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})(?:[ T](\d{1,2}):(\d{1,2}))?/)
      if (!match) return text
      const pad = (part) => String(part).padStart(2, '0')
      const date = `${match[1]}.${pad(match[2])}.${pad(match[3])}`
      return match[4] === undefined ? date : `${date} ${pad(match[4])}:${pad(match[5])}`
    },
    formatDuration(value) {
      if (value === undefined || value === null || value === '') return '--'
      if (typeof value === 'string' && /分|秒|[':]/.test(value)) return value
      const seconds = Math.max(0, Number(value) || 0)
      const minutes = Math.floor(seconds / 60)
      return `${minutes}分钟`
    },
    correctRate(record) {
      let value = record && record.correctRate
      if (value === undefined || value === null || value === '') {
        const total = Number(record && record.questionCount) || 0
        const correct = Number(record && record.correctCount) || 0
        value = total ? correct * 100 / total : 0
      }
      return Math.max(0, Math.min(100, Number(value) || 0))
    },
    gaugeValue(record) {
      if (!this.isQuestionRecord(record)) return this.numberOrDash(record && record.score)
      const rate = this.correctRate(record)
      return `${Number.isInteger(rate) ? rate : rate.toFixed(1)}%`
    },
    gaugeDasharray(record) {
      const percent = this.isQuestionRecord(record)
        ? this.correctRate(record)
        : Math.max(0, Math.min(100, Number(record && record.score) || 0))
      return `${percent} ${100 - percent}`
    },
    handleAuthFailure() {
      if (this.authExitTimer) return
      this.loading = false
      Toast('登录状态已失效，请重新进入')
      this.authExitTimer = setTimeout(() => closeSkillCenterWeb(this, 'skillCenterRecord'), 1200)
    },
    handleBack() {
      closeSkillCenterWeb(this, 'skillCenterRecord')
    },
    loadPreview() {
      this.stations = [
        {
          id: 'station-1',
          label: '第一站',
          question: { tree: [{ label: '病史采集' }] }
        },
        {
          id: 'station-2',
          label: '第二站',
          skill: { tree: [{ label: '腰椎穿刺术' }] }
        },
        {
          id: 'station-3',
          label: '第三站',
          question: { tree: [{ label: '病例分析' }] }
        }
      ]
      this.activeStationIndex = Number(this.$route.query.stationIndex) || 0
      this.allRecords = this.activeStationIndex === 1
        ? [1, 2].map((id) => ({ recordId: id, sourceType: 'EXAM', courseName: '2023年12月份腰椎穿刺术考试', duration: 1140, score: 86, studyTime: '2023-01-11 16:30' }))
        : [
          { recordId: 1, sourceType: 'STUDY', courseName: '病史采集：X线平影线理论知识', duration: 1140, questionCount: 30, correctRate: 90, studyTime: '2023-01-11 16:30' },
          { recordId: 2, sourceType: 'STUDY', courseName: '病史采集：X线平影线影像诊断', duration: 1140, questionCount: 30, correctRate: 90, studyTime: '2023-01-11 16:30' }
        ]
      this.loading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.study-record-page {
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background-color: #f8f8f8;
  background-image: url("~@/assets/images/skillCenterQuestion/header-background.png");
  background-position: top center;
  background-repeat: no-repeat;
  background-size: 100% auto;
  color: #111111;
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", "Microsoft YaHei", sans-serif;
  box-sizing: border-box;
}

.study-record-content {
  width: 100%;
  padding: 17px 17px 30px 15px;
  box-sizing: border-box;
}

.study-record-page ::v-deep .skill-header__title {
  color: #121212;
}

.station-tabs {
  display: flex;
  width: 100%;
  height: 44px;
  padding: 3px;
  overflow-x: auto;
  border-radius: 22px;
  background: #ebeef1;
  box-sizing: border-box;
  scrollbar-width: none;
}

.station-tabs::-webkit-scrollbar {
  display: none;
  width: 0;
  height: 0;
}

.station-tab {
  flex: 1 0 33.333%;
  min-width: 101px;
  height: 38px;
  padding: 0 8px;
  overflow: hidden;
  border: 0;
  border-radius: 19px;
  background: transparent;
  color: #747681;
  font-family: inherit;
  font-size: 16px;
  font-weight: 500;
  line-height: 38px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.station-tab.active {
  background: #ffffff;
  color: #000000;
  font-weight: 600;
}

.record-list {
  position: relative;
  left: 1px;
  margin-top: 21px;
}

.record-card {
  position: relative;
  height: 116px;
  margin-bottom: 10px;
  padding: 0;
  overflow: hidden;
  border-radius: 10px;
  background: #ffffff;
  box-sizing: border-box;
}

.record-card--question {
  height: 136px;
}

.record-card h2 {
  position: absolute;
  top: 12px;
  right: 80px;
  left: 16px;
  height: 22px;
  margin: 0;
  overflow: hidden;
  color: #000000;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.record-divider {
  position: absolute;
  top: 44px;
  right: 9px;
  left: 16px;
  height: 1px;
  background: #eeeeee;
}

.record-copy {
  position: absolute;
  top: 58px;
  right: 78px;
  left: 16px;
}

.record-copy p {
  height: 17px;
  margin: 0;
  margin-bottom: 6px;
  color: #333333;
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
  white-space: nowrap;
}

.record-copy p:last-child {
  margin-bottom: 0;
}

.record-gauge {
  position: absolute;
  top: 57px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
}

.record-gauge--accuracy {
  top: 58px;
  right: 23px;
  width: 60px;
  height: 60px;
}

.record-gauge svg {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
  transform: rotate(-90deg);
}

.record-gauge circle {
  fill: none;
  stroke-width: 4;
  vector-effect: non-scaling-stroke;
}

.record-gauge__track {
  stroke: #ebf1ff;
}

.record-gauge__progress {
  stroke: #0587ff;
  stroke-linecap: round;
}

.record-gauge__inner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #0587ff;
}

.record-gauge strong {
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
}

.record-gauge--accuracy strong {
  position: absolute;
  top: 12px;
  left: 0;
  width: 100%;
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  text-align: center;
}

.record-gauge small {
  position: absolute;
  top: 33px;
  left: 0;
  width: 100%;
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  text-align: center;
}

.page-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  color: #a2a6af;
  font-size: 14px;
  flex-direction: column;
}

.page-state p {
  margin: 10px 0 0;
}

.retry-button {
  margin-top: 10px;
  padding: 5px 14px;
  border: 1px solid #168df5;
  border-radius: 14px;
  background: #ffffff;
  color: #168df5;
  font-size: 13px;
}

.loading-spinner {
  width: 22px;
  height: 22px;
  border: 2px solid #dbeeff;
  border-top-color: #168df5;
  border-radius: 50%;
  animation: study-record-spin 0.8s linear infinite;
}

@keyframes study-record-spin {
  to { transform: rotate(360deg); }
}

@media (min-width: 600px) {
  .study-record-page {
    width: 375px;
    margin: 0 auto;
  }
}
</style>
